/**
 * PaidTrainingSoftGateModal — Level 2 Soft Gate
 *
 * Fires ONLY when a user initiates an action on a PAID training artifact
 * AND the artifact has capability map entries (something meaningful to advise).
 *
 * Never blocks — the user can always proceed.
 * Language: mentor-style guidance, never gatekeeper language.
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { BookOpen } from 'lucide-react';
import type { TrainingArtifact } from '@/types/training';
import type { CapabilityAdvisoryEntry } from '@/hooks/useArtifactCapabilityAdvisory';

interface PaidTrainingSoftGateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  artifact: TrainingArtifact;
  advisoryEntries: CapabilityAdvisoryEntry[];
  onProceed: () => void;
  onViewPrep: () => void;
}

export function PaidTrainingSoftGateModal({
  open,
  onOpenChange,
  artifact,
  advisoryEntries,
  onProceed,
  onViewPrep,
}: PaidTrainingSoftGateModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <DialogTitle>Before you go…</DialogTitle>
          </div>
          <DialogDescription className="text-left mt-1">
            <span className="font-medium text-foreground">"{artifact.title}"</span>{' '}
            covers material that builds on some foundational skills:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Capability list */}
          <ul className="space-y-1.5 border rounded-md p-3 bg-muted/30">
            {advisoryEntries.map((entry) => (
              <li
                key={entry.key}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 flex-shrink-0" />
                {entry.label}
              </li>
            ))}
          </ul>

          {/* Advisory copy */}
          <p className="text-sm text-muted-foreground">
            To get the most out of this training, familiarity with these topics will help.
            This is guidance only — you can always proceed.
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button className="w-full" onClick={onProceed}>
              Continue Anyway
            </Button>
            <Button variant="outline" className="w-full" onClick={onViewPrep}>
              View Helpful Prep First
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
